import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Accordion from 'react-bootstrap/Accordion';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';

import InputNumber from 'modules/_shared/components/InputNumber';
import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';

type BuyOfferSellerLimitsPanelProps = {
  sellerParticipationLimits: IncentiveOfferSellerParticipationLimits;
  /* eslint-disable-next-line no-unused-vars */
  onChangeLimit: (
    field: keyof IncentiveOfferSellerParticipationLimits,
    value: number
  ) => void;
};

const BuyOfferSellerLimitsPanel: FC<BuyOfferSellerLimitsPanelProps> = ({
  sellerParticipationLimits,
  onChangeLimit,
}) => {
  const { t } = useTranslate();
  const societyPlans = useSelector((state: State) => state.plans);

  const { showStockOptions, showPhantomShares } = useMemo(() => {
    if (!societyPlans?.length) {
      return { showStockOptions: true, showPhantomShares: true };
    }

    return {
      showStockOptions: societyPlans.some((plan) => plan.planType === 'STOCK_OPTION'),
      showPhantomShares: societyPlans.some(
        (plan) => plan.planType === 'PHANTOM_SHARE'
      ),
    };
  }, [societyPlans]);

  return (
    <div className="border rounded p-3 mt-3">
      <h6 className="overline-title text-soft mb-3">
        {t('IncentivesMarketSellerLimitsRulesTitle')}
      </h6>

      <div className="row">
        <div className="col-md-6 mb-3">
          <InputNumber
            id="seller-max-percent-unvested"
            label={t('MaximumPercentageVestedUnitsCanSold')}
            value={sellerParticipationLimits.maxPercentUnvested ?? 100}
            decimalScale={2}
            max={100}
            onChange={(value) => onChangeLimit('maxPercentUnvested', value)}
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <InputNumber
            id="seller-max-amount"
            label={t('MaximumAmountPerPartnerBeneficiary')}
            value={sellerParticipationLimits.maxAmount ?? 0}
            iconRight="icon ni ni-sign-euro"
            decimalScale={2}
            onChange={(value) => onChangeLimit('maxAmount', value)}
          />
        </div>
      </div>

      <Accordion defaultActiveKey="">
        {showStockOptions && (
          <div className="border-bottom">
            <Accordion.Toggle
              as="button"
              type="button"
              eventKey="stock-options"
              className="btn btn-link btn-block text-left d-flex align-items-center py-2 px-0"
            >
              <em className="icon ni ni-layers text-primary mr-2" />
              <span>{t('StockOptions')}</span>
              <em className="icon ni ni-chevron-down ml-auto" />
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="stock-options">
              <div className="row pb-3">
                <div className="col-md-6">
                  <InputNumber
                    id="seller-max-stocks"
                    label={t('MaximumNumber')}
                    value={sellerParticipationLimits.maxStocks ?? 0}
                    decimalScale={0}
                    onChange={(value) => onChangeLimit('maxStocks', value)}
                  />
                </div>
                <div className="col-md-6">
                  <InputNumber
                    id="seller-max-percent-stocks"
                    label={t('MaximumPercent')}
                    value={sellerParticipationLimits.maxPercentStocks ?? 0}
                    decimalScale={2}
                    max={100}
                    onChange={(value) => onChangeLimit('maxPercentStocks', value)}
                  />
                </div>
              </div>
            </Accordion.Collapse>
          </div>
        )}

        {showPhantomShares && (
          <div>
            <Accordion.Toggle
              as="button"
              type="button"
              eventKey="phantom-shares"
              className="btn btn-link btn-block text-left d-flex align-items-center py-2 px-0"
            >
              <em className="icon ni ni-coin text-primary mr-2" />
              <span>{t('PhantomShares')}</span>
              <em className="icon ni ni-chevron-down ml-auto" />
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="phantom-shares">
              <div className="row pb-3">
                <div className="col-md-6">
                  <InputNumber
                    id="seller-max-phantoms"
                    label={t('MaximumNumber')}
                    value={sellerParticipationLimits.maxPhantoms ?? 0}
                    decimalScale={0}
                    onChange={(value) => onChangeLimit('maxPhantoms', value)}
                  />
                </div>
                <div className="col-md-6">
                  <InputNumber
                    id="seller-max-percent-phantoms"
                    label={t('MaximumPercent')}
                    value={sellerParticipationLimits.maxPercentPhantoms ?? 0}
                    decimalScale={2}
                    max={100}
                    onChange={(value) =>
                      onChangeLimit('maxPercentPhantoms', value)
                    }
                  />
                </div>
              </div>
            </Accordion.Collapse>
          </div>
        )}
      </Accordion>
    </div>
  );
};

export default BuyOfferSellerLimitsPanel;
